import { Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { ProductDTO } from '../../../models/Deliveries/deliveriesDTO';
import { Subscription, Observable, BehaviorSubject } from 'rxjs';
import { TenantDataFormatDTO } from '../../../models/Tenant/Tenant.model';
import { BarcodeTypeDTO } from '../../../models/Products/productDTO';
import { FieldType } from '../../../models/custom-selection-table/custom-selection-table.model';
import { FilterName } from '../../../models/custom-table/custom-table.model';
import { Store, select } from '@ngrx/store';
import { AppState, loadedTenantState } from '../../../store/reducers/index';
import { ProductService } from '../../../shared/services/products/products/product.service';
import { SitesService } from '../../../shared/services/sites/sites.service';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '../../../shared/services/snackbar/snackbar.service';
import { SnackbarType } from '../../../models/snackbar/snackbar.model';
import { MatCheckboxChange } from '@angular/material/checkbox';

@Component({
  selector: 'app-bar-code-generator',
  templateUrl: './bar-code-generator.component.html',
  styleUrls: ['./bar-code-generator.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BarCodeGeneratorComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() siteCode: string;

  storeProductsList: ProductDTO[] = [];
  selectedProducts: ProductDTO[] = [];
  totalProducts = 0;
  searchTimer: number;

  tenantDataSub: Subscription;
  tenantData$: Observable<any>;
  tenantData: any;
  tenantFormat: TenantDataFormatDTO;

  barcodeTypes: BarcodeTypeDTO[] = [];
  selectedBarcodeType: any = null;
  selectedBarcodeTypeCode: string;
  barcodeValue: string;

  public isLoading$ = new BehaviorSubject<boolean>(false);

  searchTerms: string[] = [];
  isAllSelected = false;

  isNotFuelFilter = {
    column: "isFuel",
    fieldType: FieldType.BOOLEAN,
    operator: FilterName.EQUAL,
    value: false,
  };

  constructor(
    private store: Store<AppState>,
    private productService: ProductService,
    private sitesService: SitesService,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
  ) {
    this.tenantData$ = this.store.pipe(select(loadedTenantState));
  }

  ngOnInit(): void {
    this.loadProducts(0, 50);
    this.loadBarcodeTypes();
    this.loadTenantDateTimeFomat();
  }

  loadTenantDateTimeFomat() {
    this.tenantDataSub = this.tenantData$.subscribe((data: any) => {
      if (data) {
        this.tenantFormat = data;
      }
    });
  }

  loadProducts(skip: number, top: number): void {
    this.isLoading$.next(true);

    const paging = { skip, top, realSize: true };
    const advancedFilter: any[] = [];

    advancedFilter.push(this.isNotFuelFilter);

    if (this.selectedBarcodeType) {
      advancedFilter.push({
        column: "barcodeTypeCode",
        fieldType: FieldType.STRING,
        operator: FilterName.EQUAL,
        value: this.selectedBarcodeType,
      });
    }

    if (this.searchTerms?.length) {
      this.searchTerms.forEach((term) => {
        advancedFilter.push({
          column: "description",
          fieldType: FieldType.STRING,
          operator: FilterName.CONTAINS,
          value: term,
        });
      });
    }

    this.productService
      .getProducts(paging, null, null, { advancedFilter })
      .subscribe({
        next: (res) => {
          const products = res.result.items || [];
          this.totalProducts = res.result.size;

          this.sitesService
            .getSiteStoreProductList(this.siteCode)
            .subscribe({
              next: (siteRes) => {
                const siteProducts = siteRes?.result.items || [];
                const siteProductsMap = new Map<string, any>();
                siteProducts.forEach((sp) =>
                  siteProductsMap.set(sp.productCode, sp)
                );

                this.storeProductsList = products.map((product) => {
                  const siteData = siteProductsMap.get(product.code);
                  return {
                    ...product,
                    availableInStore: !!siteData,
                    productPrice:
                      siteData?.unitPriceGross ?? product.unitPriceGross,
                  };
                });

                this.updateSelectAllCheckboxState();
                this.isLoading$.next(false);
              },
              error: () => {
                this.storeProductsList = products.map((product) => ({
                  ...product,
                  availableInStore: false,
                  siteUnitPriceNet: null,
                }));

                this.updateSelectAllCheckboxState();
                this.isLoading$.next(false);
              },
            });
        },
        error: () => {
          this.isLoading$.next(false);
        },
      });
  }

  loadBarcodeTypes(): void {
    const paginationRequest = {
      offset: 0,
      limit: 999,
      realSize: false,
    };

    this.productService.getBarcodeTypes(paginationRequest).subscribe({
      next: (res) => {
        this.barcodeTypes = res?.result ?? res;
      },
      error: () => {
        this.isLoading$.next(false);
        this.snackbarService.show({
          message: this.translate.instant(
            "SiteManagement.barCodeDialog.loadBarCodesTypesError"
          ),
          type: SnackbarType.ERROR,
        });
      },
    });
  }

  onSelectionChange(event: any) {
    const [option] = event.options;
    const { value: product } = option;

    const index = this.selectedProducts.findIndex(
      (p) => p.code === product.code
    );
    if (index >= 0) {
      this.selectedProducts.splice(index, 1);
    } else {
      this.selectedProducts.push(product);
    }
  }

  onSelectBarcodeType(event: any): void {
    this.selectedBarcodeType = event.value;
    this.selectedProducts = [];
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadProducts(0, 50);
  }

  checkSelected(product: ProductDTO): boolean {
    return this.selectedProducts.some((p) => p.code === product.code);
  }

  selectAllChanged(event: MatCheckboxChange) {
    this.isAllSelected = event.checked;

    if (event.checked) {
      const newAllSelected = this.storeProductsList.filter(
        (p) =>
          !this.selectedProducts.some((selected) => selected.code === p.code)
      );
      this.selectedProducts = [...this.selectedProducts, ...newAllSelected];
    } else {
      this.selectedProducts = this.selectedProducts.filter(
        (selected) =>
          !this.storeProductsList.some((p) => p.code === selected.code)
      );
    }
  }

  updateSelectAllCheckboxState(): void {
    this.isAllSelected = this.storeProductsList.every((product) =>
      this.selectedProducts.some((selected) => selected.code === product.code)
    );
  }

  searchStoreProducts(terms: string[]) {
    this.searchTerms = terms;
    this.loadProducts(0, 50);
  }

  getPagedData() {
    const { pageIndex, pageSize } = this.paginator;
    this.loadProducts(pageIndex * pageSize, pageSize);
    this.isAllSelected = false;
  }

  onPrintClick(): void {
    const payload = {
      settings: {
        format: "PDF",
        formatter: {
          dateTimeFormat: this.tenantFormat?.datetimeFormat,
          dateFormat: this.tenantFormat?.dateFormat,
          amountDecimalPlaces: this.tenantFormat?.amountDecimalPlaces,
          unitPriceDecimalPlaces: this.tenantFormat?.unitPriceDecimalPlaces,
          forecourtUomDecimalPlaces:
            this.tenantFormat?.forecourtUomDecimalPlaces,
          forecourtUnitPriceDecimalPlaces:
            this.tenantFormat?.forecourtUnitPriceDecimalPlaces,
          currencyIso: this.tenantFormat?.currency?.code,
          currencySymbol: this.tenantFormat?.currency?.symbol,
        },
      },
      barcodeType: this.selectedBarcodeTypeCode,
      products: this.selectedProducts.map((p) => p.code),
    };

    this.isLoading$.next(true);

    this.productService.printBarcodes(payload).subscribe({
      next: (response) => {
        const pdfBase64 = response?.data;
        if (!pdfBase64) {
          this.isLoading$.next(false);
          return;
        }

        const pdfSrc = response.data;
        const byteArray = new Uint8Array(
          atob(pdfSrc)
            .split("")
            .map((char) => char.charCodeAt(0))
        );
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");

        this.isLoading$.next(false);
      },
      error: (response) => {
        this.isLoading$.next(false);

        const sourceCode = response;
        const fallbackMessage = this.translate.instant(
          "SiteManagement.barCodeDialog.printBarCodeError"
        );

        this.snackbarService.show({
          message: sourceCode
            ? this.translate.instant(sourceCode)
            : fallbackMessage,
          type: SnackbarType.ERROR,
        });
      },
    });
  }

  setStoreProductSearchTooltip(): string {
    const searchTooltipHint = this.translate.instant("SimpleFilter.HintTitle");
    const searchableFields = [
      this.translate.instant("SiteManagement.Code"),
      this.translate.instant("SiteManagement.Description"),
    ];
    return `${searchTooltipHint}\n${searchableFields.join("\n")}`;
  }

}
